import React from "react";
import LegalLayout from "./LegalLayout";
import { BRAND } from "../../lib/brand";

const APP = BRAND.name;
const EMAIL = BRAND.legal.contactEmail;

const SECTIONS = [
    {
        id: "spirit",
        title: "The spirit of the place",
        body: (
            <>
                <p>
                    {APP} is a community of real people who actually go outside. Our guidelines exist to keep this place
                    welcoming, useful, and safe &mdash; both online and on the trail.
                </p>
                <p>
                    We&rsquo;d rather have one honest trail report than a thousand polished posts. Be kind. Be accurate. Leave
                    the trail (and the conversation) better than you found it.
                </p>
            </>
        ),
    },
    {
        id: "encouraged",
        title: "What we encourage",
        body: (
            <>
                <ul>
                    <li><strong>Real reports.</strong> Share what you actually experienced &mdash; conditions, hazards, what went wrong, what to bring.</li>
                    <li><strong>Helpful detail.</strong> Difficulty, timing, water sources, parking, permits, alternative routes.</li>
                    <li><strong>Photos with context.</strong> Captions and locations help others recreate the trip safely.</li>
                    <li><strong>Constructive feedback.</strong> Disagreement is fine; do it respectfully and with specifics.</li>
                    <li><strong>Leave No Trace.</strong> Promote ethical use of public lands; pack it in, pack it out, respect wildlife.</li>
                </ul>
            </>
        ),
    },
    {
        id: "prohibited",
        title: "What is not allowed",
        body: (
            <>
                <h3>Harassment, hate, and threats</h3>
                <ul>
                    <li>Personal attacks, slurs, doxxing, or content that targets people based on race, ethnicity, national origin, religion, gender, gender identity, sexual orientation, age, disability, or veteran status.</li>
                    <li>Direct threats of violence or content that incites violence.</li>
                </ul>
                <h3>Sexual content and content involving minors</h3>
                <ul>
                    <li>Pornographic, sexually explicit, or sexually suggestive content.</li>
                    <li>Any content that sexualises a minor &mdash; this is reported to authorities (NCMEC) immediately.</li>
                </ul>
                <h3>Illegal activity</h3>
                <ul>
                    <li>Promotion or facilitation of illegal acts, including poaching, illegal hunting, illegal harvest of plants or wildlife, drug sales, or illegal weapon transactions.</li>
                    <li>Trespass into closed land, sacred sites, or protected areas; encouragement to do the same.</li>
                </ul>
                <h3>Dangerous misinformation</h3>
                <ul>
                    <li>Fabricated trip reports, fake GPS routes, fake summit claims, or false safety information.</li>
                    <li>Misleading difficulty ratings or condition reports likely to put others at risk.</li>
                </ul>
                <h3>Sensitive locations</h3>
                <ul>
                    <li>Do not publish coordinates, photos, or descriptions that disclose archaeological sites, sacred Indigenous lands, raptor nests, dens, or other locations where exposure causes harm. When in doubt, generalise the location.</li>
                </ul>
                <h3>Spam, scams, and self-promotion abuse</h3>
                <ul>
                    <li>Unsolicited advertising, link spam, manipulation of likes, saves, or comments.</li>
                    <li>Fake accounts, impersonation, or coordinated inauthentic behaviour.</li>
                </ul>
                <h3>Privacy and intellectual property</h3>
                <ul>
                    <li>Posting another person&rsquo;s private information (address, phone, image, location) without consent.</li>
                    <li>Posting copyrighted photos, music, or video you don&rsquo;t have the right to share.</li>
                </ul>
                <h3>Marketplace</h3>
                <ul>
                    <li>Listings of stolen goods, recalled items, weapons sold in violation of law, or any item prohibited by our <a href="/terms">Terms of Service</a>.</li>
                    <li>Misrepresenting the condition or provenance of an item.</li>
                </ul>
            </>
        ),
    },
    {
        id: "wildlife",
        title: "Wildlife and conservation",
        body: (
            <>
                <p>
                    Outdoor spaces are shared with animals, plants, and communities older than any of us. Posts that depict
                    illegal harvest, harassment of wildlife, off-trail damage, or disregard for permits or seasonal closures will
                    be removed. We support the principles of <em>Leave No Trace</em> and respect for tribal sovereignty over
                    cultural and sacred sites.
                </p>
            </>
        ),
    },
    {
        id: "safety",
        title: "Safety information is a courtesy, not a guarantee",
        body: (
            <>
                <p>
                    Trip reports, route data, and difficulty ratings on {APP} are <strong>user-generated</strong>. They can be
                    out of date or wrong. Verify with current land-manager information, weather, and conditions before you go.
                    {APP} is not a search-and-rescue service; in an emergency, call your local emergency number.
                </p>
                <p>
                    See our <a href="/terms">Terms of Service</a> for the full assumption-of-risk language.
                </p>
            </>
        ),
    },
    {
        id: "reporting",
        title: "Reporting and enforcement",
        body: (
            <>
                <p>
                    Use the <strong>Report</strong> action on any post, comment, message, listing, or profile to flag something
                    you believe violates these guidelines. Reports are reviewed by trained moderators. You can also email{" "}
                    <a href={`mailto:${EMAIL}`}>{EMAIL}</a>.
                </p>
                <h3>What we may do</h3>
                <ul>
                    <li><strong>Educate</strong> &mdash; a private note explaining the issue.</li>
                    <li><strong>Remove</strong> the offending content.</li>
                    <li><strong>Restrict</strong> features (e.g., temporary inability to comment, post, or message).</li>
                    <li><strong>Suspend</strong> the account for a defined period.</li>
                    <li><strong>Permanently ban</strong> for severe or repeated violations.</li>
                    <li><strong>Refer to authorities</strong> when content depicts imminent harm, child exploitation, or other criminal conduct.</li>
                </ul>
                <p>
                    We aim for proportionate responses, but we may skip steps when content presents an immediate risk of harm.
                </p>
            </>
        ),
    },
    {
        id: "appeals",
        title: "Appeals",
        body: (
            <>
                <p>
                    If your content was removed or your account restricted and you believe we made a mistake, reply to the
                    moderation notice or email <a href={`mailto:${EMAIL}`}>{EMAIL}</a> with the relevant URL and a short
                    explanation. We aim to respond within 5 business days. EU users have additional appeal rights under the
                    Digital Services Act, including the right to escalate to an out-of-court dispute settlement body.
                </p>
            </>
        ),
    },
    {
        id: "contact",
        title: "Contact us",
        body: (
            <>
                <p>
                    Questions, ideas, or trust-and-safety concerns: <a href={`mailto:${EMAIL}`}>{EMAIL}</a>.
                </p>
                <p>
                    Thank you for helping us build a community worth showing up for.
                </p>
            </>
        ),
    },
];

export default function CommunityGuidelines() {
    return (
        <LegalLayout
            testid="community-guidelines-page"
            title="Community Guidelines"
            lede={`A short, plain-English version of how we keep ${APP} useful and safe — for every member, every trail, and every adventure shared here.`}
            sections={SECTIONS}
        />
    );
}
